import type { RiveParameters } from "@rive-app/canvas";
import { Fit, Layout, Rive } from "@rive-app/canvas";

/** Controller for an animation. */
export default class Animation {
	//Animations are not shown on iPhones
	private dontShowSelector = "body.iphone";

	/**
	 * Initialize the animation.
	 *
	 * @param container The container element to mount the animation in.
	 * @param animationName The name of the animation file.
	 * @param assetFolder The folder where the animation file is located.
	 * @param riveParameters Additional parameters to pass to the Rive
	 *   constructor.
	 */
	constructor(
		protected container: HTMLCanvasElement,
		protected animationName: string,
		protected assetFolder: string | null = null,
		protected riveParameters: Exclude<
			Partial<RiveParameters>,
			"src" | "canvas"
		> = {},
	) {
		if (document.querySelector(this.dontShowSelector)) {
			return;
		}
		if (!Animation.isHidden(this.container)) {
			this.mountCanvas(this.container);
		} else {
			const observerOptions = {
				childList: true,
				subtree: true,
				attributes: true,
			};
			const elementToWatch = document.querySelector("body");
			if (!elementToWatch) {
				return;
			}
			const observer = new MutationObserver(() => {
				if (!Animation.isHidden(this.container)) {
					observer.disconnect();
					this.mountCanvas(this.container);
				}
			});
			observer.observe(elementToWatch, observerOptions);
		}
	}

	/**
	 * Determines if an animation is currently not visible
	 *
	 * @param el The element to check
	 * @returns Whether the element is hidden
	 */
	protected static isHidden(el: HTMLElement): boolean {
		return el.offsetParent === null;
	}

	/**
	 * Hides the placeholder image once the animation is loaded.
	 *
	 * @param canvas The canvas element
	 */
	private static hidePlaceholder(canvas: HTMLCanvasElement) {
		const previousSibling = canvas.previousElementSibling;
		if (previousSibling instanceof HTMLImageElement) {
			previousSibling.style.display = "none";
		}
	}

	/**
	 * Mounts the animation to the canvas element.
	 *
	 * @param canvas The canvas element to mount the animation to.
	 */
	private mountCanvas(canvas: HTMLCanvasElement) {
		canvas.style.display = "block";
		const layout = new Layout({
			fit: Fit.Fill,
		});

		const params: RiveParameters = {
			src: this.assetFolder
				? `${this.assetFolder}${this.animationName}.riv`
				: this.animationName,
			canvas,
			autoplay: true,
			layout,
			...this.riveParameters,
		};

		new Rive(params);

		canvas.classList.remove("unloaded");
		canvas.classList.add("loaded");
		Animation.hidePlaceholder(canvas);
	}
}
